import { WebCompatibleWebView } from '@/src/common/components/WebCompatibleWebView'
import { useLogError } from '@/src/common/hooks/useLogError'
import { routerBack } from '@/src/common/utils/routerBack'
import { useAlert } from '@/src/components/ui/elements/dialogs/useAlert'
import { useState } from 'react'
import { Platform, TouchableOpacity, View } from 'react-native'
import { useEvent } from 'tamagui'
import { Ionicons } from '@expo/vector-icons'
import * as FileSystem from 'expo-file-system'
import * as Sharing from 'expo-sharing'
import {
  useCardManagementStackScreensAreModals,
  useCardStackScreen,
} from '@/src/app/main/(tabs)/card/card-management/_common/cardStackScreen'
import { maxWidth } from '@/src/common/wrappers/MainWrapper'
import { openInAppBrowser } from '@/src/common/utils/openInAppBrowser'
import {
  CardPaymentsAgreementStoreSchema,
  useCardPaymentsParameterStore,
} from '../statsig/store/useCardPaymentsParameterStore'
import { CardPaymentsE2E } from '../e2e'

export function useAutoPayAgreementScreenProps() {
  const Alert = useAlert(AutoPayAgreement.name)
  const logError = useLogError()
  const handleError = useEvent(() => {
    Alert.alert(
      'Error',
      'We had an issue fetching ACH Authorization and Agreement. Please try again later.',
    )
    void routerBack()
  })
  const pdfName = `Mesa ACH Authorization and Agreement.pdf`
  const { url } = CardPaymentsAgreementStoreSchema.parse(
    useCardPaymentsParameterStore().get('agreement', {
      url: 'https://assets.mesamember.com/card_payments/ach-authorization-agreement.pdf',
    }),
  )

  async function downloadAgreement() {
    try {
      await downloadAgreementFile(url, pdfName)
    } catch (error) {
      logError('ACH Authorization and Agreement Download failed', error)
    }
  }

  const cardManagementStackScreensAreModals = useCardManagementStackScreensAreModals()
  return {
    ...useCardStackScreen({
      name: 'autoPay/agreement',
      options: {
        title: 'Auto-pay Terms',
        ...(cardManagementStackScreensAreModals ? { presentation: 'transparentModal' } : undefined),
        headerShown: false,
      },
      sectionProps: {
        backProps: {
          testID: 'InAppBrowser.Press.Back',
        },
        onBackPress: () => void routerBack(),
        closeStyle: {
          position: 'relative',
          right: 0,
        },
        right(close) {
          return (
            <View
              style={{
                flexDirection: 'row',
                alignItems: 'center',
                gap: 16,
                position: 'absolute',
                right: 16,
              }}
            >
              {Platform.OS !== 'web' && (
                <TouchableOpacity onPress={() => void downloadAgreement()}>
                  <Ionicons name="download-outline" size={24} color="white" />
                </TouchableOpacity>
              )}
              {close}
            </View>
          )
        },
      },
      renderTitleInHeader() {
        return 'Auto-pay Terms'
      },
    }),
    url,
    logError,
    handleError,
  } as const
}

const downloadAgreementFile = (
  Platform.OS === 'web'
    ? async (url, pdfName) => {
        try {
          const response = await fetch(url, { mode: 'no-cors' })
          const link = document.createElement('a')
          link.href = URL.createObjectURL(await response.blob())
          link.download = pdfName
          link.click()
        } catch {
          await openInAppBrowser(url, pdfName, 'tab')
        }
      }
    : async (url, pdfName) => {
        if (!FileSystem.documentDirectory) {
          throw new Error(
            'Document directory is not available when downloading ACH Authorization and Agreement',
          )
        }

        const downloadResumable = FileSystem.createDownloadResumable(
          url,
          FileSystem.documentDirectory + pdfName,
          {},
        )

        const result = await downloadResumable.downloadAsync()

        if (result?.uri) {
          await Sharing.shareAsync(result.uri)
        }
      }
) satisfies (url: string, pdfName: string) => Promise<void>

export default function AutoPayAgreement() {
  const { Modal, url, options, logError, handleError } = useAutoPayAgreementScreenProps()
  const [loaded, setLoaded] = useState(false)

  return Modal({
    contentContainerProps: { style: { width: maxWidth, height: '88%' } },
    children: (
      <>
        {options.header()}
        <WebCompatibleWebView
          onError={(evt) => {
            logError(
              new Error('Failed to render ACH Authorization and Agreement PDF', {
                cause: evt.nativeEvent,
              }),
            )
            handleError()
          }}
          source={url}
          containerStyle={{ flex: 1 }}
          onLoad={() => setLoaded(true)}
          testID={loaded ? CardPaymentsE2E.Ids.View.AutoPayAgreement : undefined}
        />
      </>
    ),
  })
}

export const AutoPayAgreementScreen = {
  name: 'auto-pay-agreement',
  Screen: AutoPayAgreement,
} as const
